import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { apiGet, operationsClient } from '@/lib/api';
import { jobAnalyticsReportEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';
import { dateKeyInTimeZone } from '@/lib/date-values';
import { exportRowsAsXLSX } from '@/lib/export';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import { NumberInput } from '@/components/molecules/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  RevenueHotspotMap,
  type RevenueHotspotArea,
} from '@/components/organisms/analytics-reports/RevenueHotspotMap';
import {
  AnalyticsReportShell,
  BreakdownTable,
  MetricCard,
  ReportDateRangeControl,
  ReportLineChart,
  ReportSection,
  ReportTeamFilter,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatInteger,
  formatPercent,
  useDrillToList,
  useReportPeriod,
  type ScorecardColumn,
  type TeamFilterOption,
} from '@/components/organisms/analytics-reports';

interface JobOverview {
  totalJobs: number;
  totalRevenue: number;
  completedRevenue: number;
  jobAverage: number;
  profit: number;
  marginPct: number;
  avgCostPerJob: number;
}

interface JobScorecardRow {
  id: string;
  name: string;
  jobs: number;
  revenue: number;
  jobAverage: number;
  profit: number;
  marginPct: number;
}

interface JobCostCategory {
  category: string;
  amount: number;
  pctOfRevenue: number;
}

interface JobSalesReport {
  period: { from: string; to: string };
  overview: JobOverview;
  overviewPrevious: JobOverview;
  bySalesperson: JobScorecardRow[];
  byTechnician: JobScorecardRow[];
  costByCategory: JobCostCategory[];
  trend: { period: string; value: number }[];
  teamOptions: TeamFilterOption[];
}

interface JobHotspotReport {
  period: { from: string; to: string };
  lowVolumeThreshold: number;
  mappedJobCount: number;
  unmappedJobCount: number;
  totalRevenue: number;
  areas: RevenueHotspotArea[];
}

/** Report codes from the job cost ledger (expense-type codes for expense rows). */
const COST_CATEGORY_LABELS: Record<string, string> = {
  MATERIALS: 'Materials',
  EQUIPMENT: 'Equipment',
  EQUIPMENT_RENTAL: 'Equipment rental',
  SUBCONTRACTOR: 'Subcontractors',
  TRAVEL: 'Travel',
  PERMIT: 'Permits',
  DISPOSAL: 'Disposal',
  FREIGHT: 'Freight',
  OTHER: 'Other',
};

export const Route = createFileRoute('/_dashboard/dashboard/jobs/reports')({
  component: JobReportsPage,
});

/** Mirrors `UNATTRIBUTED_SCORECARD_ID` in job-analytics-reports.service.ts. */
const UNATTRIBUTED_SCORECARD_ID = 'unattributed';
const isUnattributedRow = (row: JobScorecardRow) => row.id === UNATTRIBUTED_SCORECARD_ID;

function scorecardColumns(label: string): ScorecardColumn<JobScorecardRow>[] {
  return [
    { key: 'name', header: label, type: 'text', value: (r) => r.name },
    {
      key: 'jobs',
      header: 'Jobs',
      type: 'number',
      value: (r) => r.jobs,
      total: (rows) => rows.reduce((s, r) => s + r.jobs, 0),
    },
    {
      key: 'revenue',
      header: 'Revenue',
      type: 'currency',
      value: (r) => r.revenue,
      total: (rows) => rows.reduce((s, r) => s + r.revenue, 0),
    },
    { key: 'jobAverage', header: 'Job Avg', type: 'currency', value: (r) => r.jobAverage },
    {
      key: 'profit',
      header: 'Profit',
      type: 'currency',
      value: (r) => r.profit,
      total: (rows) => rows.reduce((s, r) => s + r.profit, 0),
    },
    { key: 'marginPct', header: 'Margin', type: 'percent', value: (r) => r.marginPct },
  ];
}

function JobReportsPage() {
  const period = useReportPeriod('analytics.jobs.reportPeriod', '30d');
  const companyTz = useCompanyTimezone();
  const [technicianId, setTechnicianId] = useState<string | undefined>(undefined);
  const [hotspotJobTypeId, setHotspotJobTypeId] = useState<string | undefined>();
  const [lowVolumeThreshold, setLowVolumeThreshold] = useState(3);
  const jobTypes = useJobTypes();
  const params = { from: period.from, to: period.to, technicianId };
  const drillTo = useDrillToList();

  const q = useApiQuery<JobSalesReport>({
    queryKey: jobKeys.report('sales-report', params),
    queryFn: () =>
      apiGet<JobSalesReport>(operationsClient, jobAnalyticsReportEndpoints.salesReport(params)),
    showErrorToast: false,
  });
  const data = q.data;
  const cur = data?.overview;
  const prev = data?.overviewPrevious;
  const hotspotParams = {
    from: period.from,
    to: period.to,
    technicianId,
    jobTypeId: hotspotJobTypeId,
    lowVolumeThreshold,
  };
  const hotspots = useApiQuery<JobHotspotReport>({
    queryKey: jobKeys.report('revenue-hotspots', hotspotParams),
    queryFn: () =>
      apiGet<JobHotspotReport>(
        operationsClient,
        jobAnalyticsReportEndpoints.hotspots(hotspotParams),
      ),
    showErrorToast: false,
  });

  async function handleExport() {
    if (!data) return;
    const rows = [
      ...data.byTechnician.map((r) => ({
        Group: 'Technician',
        Name: r.name,
        Jobs: r.jobs,
        Revenue: r.revenue,
        'Job Avg': r.jobAverage,
        Profit: r.profit,
        'Margin %': formatPercent(r.marginPct),
      })),
      ...data.bySalesperson.map((r) => ({
        Group: 'Salesperson',
        Name: r.name,
        Jobs: r.jobs,
        Revenue: r.revenue,
        'Job Avg': r.jobAverage,
        Profit: r.profit,
        'Margin %': formatPercent(r.marginPct),
      })),
    ];
    const fname = `jobs-report-${dateKeyInTimeZone(period.from, companyTz)}_${dateKeyInTimeZone(
      period.to,
      companyTz,
    )}.xlsx`;
    await exportRowsAsXLSX(rows, fname, { currencyColumns: ['Revenue', 'Job Avg', 'Profit'] });
  }

  return (
    <AnalyticsReportShell
      title="Job Reports"
      subtitle="Sales performance and analytics"
      backTo="/dashboard/jobs"
      onExport={handleExport}
      exportDisabled={!data}
      actions={
        <>
          <ReportTeamFilter
            value={technicianId}
            onChange={setTechnicianId}
            options={data?.teamOptions ?? []}
            allLabel="All technicians"
          />
          <ReportDateRangeControl period={period} />
        </>
      }
    >
      <ReportSection title="Sales overview">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <MetricCard
            title="Total Jobs"
            value={cur ? formatInteger(cur.totalJobs) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(cur?.totalJobs, prev?.totalJobs)}
          />
          <MetricCard
            title="Total Revenue"
            value={cur ? formatCurrency(cur.totalRevenue) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(cur?.totalRevenue, prev?.totalRevenue)}
          />
          <MetricCard
            title="Completed Revenue"
            value={cur ? formatCurrency(cur.completedRevenue) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(cur?.completedRevenue, prev?.completedRevenue)}
          />
          <MetricCard
            title="Job Average"
            value={cur ? formatCurrency(cur.jobAverage) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(cur?.jobAverage, prev?.jobAverage)}
          />
          <MetricCard
            title="Profit"
            value={cur ? formatCurrency(cur.profit) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(cur?.profit, prev?.profit)}
          />
          <MetricCard
            title="Margin"
            value={cur ? formatPercent(cur.marginPct) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(cur?.marginPct, prev?.marginPct)}
          />
          <MetricCard
            title="Avg Cost / Job"
            value={cur ? formatCurrency(cur.avgCostPerJob) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(cur?.avgCostPerJob, prev?.avgCostPerJob)}
            deltaPositiveIsGood={false}
          />
        </div>
      </ReportSection>

      <ReportSection
        title="By technician"
        description="All jobs created in the range, grouped by the technician on the job's earliest visit — who performed the work, not who sold it (not limited to completed jobs). Jobs with no technician visit yet are shown as Unattributed."
      >
        <ScorecardTable<JobScorecardRow>
          rows={data?.byTechnician ?? []}
          isLoading={q.isLoading}
          emptyMessage="No jobs in this range"
          initialSortKey="revenue"
          columns={scorecardColumns('Technician')}
          pinLast={isUnattributedRow}
          // The Unattributed bucket is not a person — there is nobody to
          // drill into, so it is neither clickable nor styled as such.
          isRowClickable={(r) => !isUnattributedRow(r)}
          onRowClick={(r) =>
            drillTo('jobs', { assignedTo: r.id, dateFrom: period.from, dateTo: period.to })
          }
        />
      </ReportSection>

      <ReportSection
        title="By salesperson"
        description="Grouped by the job's sales rep — inherited from the estimate's closer when the job was sold from one. Jobs with no sales rep are shown as Unattributed."
      >
        <ScorecardTable<JobScorecardRow>
          rows={data?.bySalesperson ?? []}
          isLoading={q.isLoading}
          emptyMessage="No jobs in this range"
          initialSortKey="revenue"
          columns={scorecardColumns('Salesperson')}
          pinLast={isUnattributedRow}
        />
      </ReportSection>

      <ReportSection
        title="Job costing by category"
        description="Recorded cost (approved expenses and received purchase orders) grouped by category, with each category as a share of total revenue."
      >
        <BreakdownTable
          rows={data?.costByCategory ?? []}
          isLoading={q.isLoading}
          emptyMessage="No costs recorded in this range"
          columns={[
            {
              key: 'category',
              header: 'Category',
              align: 'left',
              render: (r) => COST_CATEGORY_LABELS[r.category] ?? r.category,
            },
            {
              key: 'amount',
              header: 'Cost',
              align: 'right',
              render: (r) => formatCurrency(r.amount),
            },
            {
              key: 'pct',
              header: '% of Revenue',
              align: 'right',
              render: (r) => formatPercent(r.pctOfRevenue),
            },
          ]}
        />
      </ReportSection>

      <ReportSection title="Revenue trend">
        <ReportLineChart
          title="Revenue by month"
          valueFormat="currency"
          isLoading={q.isLoading}
          points={(data?.trend ?? []).map((p) => ({ period: p.period, value: p.value }))}
        />
      </ReportSection>

      <ReportSection
        title="Revenue hot spots"
        description="Completed jobs with issued invoices. Color shows average transaction size; numbered markers show job count."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">Job type</label>
            <Select
              value={hotspotJobTypeId ?? 'all'}
              onValueChange={(value) => setHotspotJobTypeId(value === 'all' ? undefined : value)}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All job types</SelectItem>
                {(jobTypes.data ?? [])
                  .filter((jobType) => jobType.isActive)
                  .map((jobType) => (
                    <SelectItem key={jobType.id} value={jobType.id}>
                      {jobType.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label htmlFor="hotspot-threshold" className="text-sm font-medium">
              Low-volume threshold
            </label>
            <NumberInput
              id="hotspot-threshold"
              min={1}
              max={100}
              className="w-28"
              value={lowVolumeThreshold}
              // Out-of-range keystrokes are ignored, so blur settles the box back
              // on the threshold in force. Clearing it parks at the floor rather
              // than refusing the backspace.
              emptyValue={1}
              onChange={(next) => {
                if (next !== null && Number.isInteger(next) && next >= 1 && next <= 100) {
                  setLowVolumeThreshold(next);
                }
              }}
            />
          </div>
          <div className="ml-auto flex gap-6 text-sm">
            <div>
              <div className="text-muted-foreground">Mapped jobs</div>
              <div className="font-semibold">
                {hotspots.data ? formatInteger(hotspots.data.mappedJobCount) : '--'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Unmapped</div>
              <div className="font-semibold">
                {hotspots.data ? formatInteger(hotspots.data.unmappedJobCount) : '--'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Revenue</div>
              <div className="font-semibold">
                {hotspots.data ? formatCurrency(hotspots.data.totalRevenue) : '--'}
              </div>
            </div>
          </div>
        </div>
        <RevenueHotspotMap
          areas={hotspots.data?.areas ?? []}
          isLoading={hotspots.isLoading}
          onAreaClick={(area) =>
            drillTo('jobs', {
              hotspotPostalArea: area.postalArea,
              hotspotJobTypeId,
              hotspotTechnicianId: technicianId,
              hotspotCountry: area.country,
              hotspotRegionCode: area.regionCode,
              dateFrom: period.from,
              dateTo: period.to,
            })
          }
        />
      </ReportSection>
    </AnalyticsReportShell>
  );
}
